import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit2, Trash2, Download, MoreHorizontal, 
  Building2, User, Phone, MapPin, FileText, ExternalLink, Plus, X, Save, Check, AlertCircle, Lock, Mail, Send, Loader2, Server
} from 'lucide-react';
import { leadsAPI, otaAPI, settingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { toast } from 'sonner';
import BankDetailsCard from '../components/BankDetailsCard';
import PefPdfCard from '../components/PefPdfCard';
// Phase 19.3 — DynamicCustomFields no longer rendered on Account Details
// page. Backend (custom_fields API + Settings → Account Form Builder)
// remains intact; data persistence is unchanged.
import LabelSelector from '../components/LabelSelector';
import LeadTasksTab from '../components/LeadTasksTab';
import OTATimerControl from '../components/OTATimerControl';
import CommercialApproval from '../components/CommercialApproval';
import { setupClearingTermsLabel } from '../components/LeadModal';
import ChecklistSLATracker from '../components/ChecklistSLATracker';
import OTAChecklistSLATracker, { OTAChecklistBadge } from '../components/OTAChecklistSLATracker';
import { useOtaOptions } from '../hooks/useOtaOptions';
import { formatCommission } from '../lib/utils';
import { userHasAnyRole, userHasRole } from '../utils/roleHelpers';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  PROPERTY_STATUS_OPTIONS,
  LEAD_STAGE_OPTIONS,
  INDIAN_STATES,
  WEBSITE_AVAILABLE_OPTIONS,
  EXISTING_OTA_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  INTERESTED_PLAN_OPTIONS,
  SETUP_PAYMENT_STATUS_OPTIONS,
  CHECKLIST_STATUS_OPTIONS,
  OTA_NAME_OPTIONS,
  OTA_STATUS_OPTIONS,
  ACCESS_TO_HOTEL_OPTIONS,
  getAvailableOTAOptions,
  getEmptyOTAItem,
  getEmptyPropertyChecklist,
  MAX_OTA_ENTRIES
} from '../constants/dropdownOptions';

// Tab Button Component
const TabButton = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
      active 
        ? 'bg-white text-teal-600 border-t border-l border-r border-gray-200 -mb-px' 
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

// Card Component
const InfoCard = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <h3 className="font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

// iter312 — Normalize URL for anchor `href`. If the value already starts
// with an http(s) scheme, use it as-is; otherwise prefix with `https://`.
// Returns empty string for falsy input so anchors never render a broken
// `href` attribute.
const normalizeHref = (value) => {
  if (!value) return '';
  const s = String(value).trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

// iter312 — URL validation for OTA listing_url on save. Accepts absolute
// http(s) URLs OR a schemeless host (`example.com/path`) which the
// normalizer will fix on render. Returns null when the value is empty
// (empty is allowed — the field is optional). Returns an error string
// when the URL is malformed so the caller can toast/block the save.
const validateListingUrl = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const u = new URL(candidate);
    if (!u.hostname || u.hostname.length < 3 || !u.hostname.includes('.')) {
      return 'Enter a valid URL (e.g. https://example.com/listing-123).';
    }
    return null;
  } catch {
    return 'Enter a valid URL (e.g. https://example.com/listing-123).';
  }
};

// Info Row Component
const InfoRow = ({ label, value, isLink = false }) => (
  <div className="flex py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 w-44 flex-shrink-0">{label}</span>
    {isLink && value ? (
      <a 
        href={normalizeHref(value)} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-sm text-teal-600 hover:text-teal-800 flex items-center gap-1 min-w-0 break-all"
      >
        {value} <ExternalLink size={12} />
      </a>
    ) : (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    )}
  </div>
);

// Badge Component for Status
const StatusBadge = ({ value, type = 'default' }) => {
  const getColors = () => {
    switch (type) {
      case 'stage':
        switch (value?.toUpperCase()) {
          case 'NEW': return 'bg-green-100 text-green-800';
          case 'CONTACTED': return 'bg-blue-100 text-blue-800';
          case 'HOT': return 'bg-red-100 text-red-800';
          case 'CLOSED WON': return 'bg-indigo-100 text-indigo-800';
          case 'CLOSED LOST': return 'bg-gray-100 text-gray-800';
          default: return 'bg-amber-100 text-amber-800';
        }
      case 'status':
        switch (value) {
          case 'Complete': case 'Completed': case 'Granted': return 'bg-green-100 text-green-800';
          case 'Pending': case 'PROGRESS': return 'bg-amber-100 text-amber-800';
          case 'N/A': case 'NA': return 'bg-gray-100 text-gray-600';
          case 'Active': return 'bg-green-100 text-green-800';
          case 'Inactive': return 'bg-red-100 text-red-800';
          default: return 'bg-blue-100 text-blue-800';
        }
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!value) return <span className="text-sm text-gray-400">-</span>;
  
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getColors()}`}>
      {value}
    </span>
  );
};

// Select Field for Edit Modal
const SelectField = ({ label, name, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

const KNOWN_SOURCE_VALUES = LEAD_SOURCE_OPTIONS.filter(o => o.value && o.value !== '__other__').map(o => o.value);

const LeadSourceField = ({ value, onChange }) => {
  const isCustom = value && !KNOWN_SOURCE_VALUES.includes(value);
  const [showOtherInput, setShowOtherInput] = useState(isCustom);
  const [customText, setCustomText] = useState(isCustom ? value : '');

  // Update state when value prop changes (e.g., when modal opens with different lead)
  useEffect(() => {
    const newIsCustom = value && !KNOWN_SOURCE_VALUES.includes(value);
    setShowOtherInput(newIsCustom);
    setCustomText(newIsCustom ? value : '');
  }, [value]);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === '__other__') {
      setShowOtherInput(true);
      setCustomText('');
      onChange('');
    } else {
      setShowOtherInput(false);
      setCustomText('');
      onChange(v);
    }
  };

  const handleCustom = (e) => {
    const v = e.target.value;
    setCustomText(v);
    onChange(v);
  };

  const dropdownValue = showOtherInput ? '__other__' : (KNOWN_SOURCE_VALUES.includes(value) ? value : '');

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
      <select
        value={dropdownValue}
        onChange={handleSelect}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
        data-testid="lead-source-select"
      >
        {LEAD_SOURCE_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {showOtherInput && (
        <input
          type="text"
          value={customText}
          onChange={handleCustom}
          placeholder="Enter Lead Source"
          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
          data-testid="lead-source-custom-input"
        />
      )}
    </div>
  );
};

// Edit Lead Modal Component
const EditLeadModal = ({ isOpen, onClose, onSave, lead, section }) => {
  const { user: editAuthUser } = useAuth();
  const editIsAdmin = (editAuthUser?.role || '').toLowerCase() === 'admin';
  const [formData, setFormData] = useState({});
  // Iter239 — dynamic OTA dropdown source shared with Add Account modal.
  // `historicalValues` keeps saved OTA names visible even if they've been
  // removed from the current OTA Configuration.
  const historicalOtaValues = React.useMemo(
    () => (lead?.ota_info || []).map(o => o.ota_name).filter(Boolean),
    [lead]
  );
  const { options: dynamicOtaOptions } = useOtaOptions(historicalOtaValues);
  // Mirror Add Account commercials — plans from /api/account-plans drive the
  // Interested Plan dropdown + auto-fill of One Time Setup Fee.
  const [accountPlans, setAccountPlans] = useState([]);
  const [isSaving, setIsSaving] = useState(false); // in-flight save guard
  
  useEffect(() => {
    if (lead && isOpen) {
      // iter277b — Heal legacy checklist values on load so the edit form's
      // dropdown pre-fills correctly. Imports before iter277 wrote
      // "Completed" (with a trailing 'd'), and some sources uppercase it
      // to "COMPLETED". Both spellings render as blank/"Pending" in the
      // <select> because CHECKLIST_STATUS_OPTIONS only lists "Complete".
      // Translate both variants to the canonical "Complete" — every other
      // value passes through untouched, so no silent semantic rewrites.
      const rawChecklist = lead.property_checklist_info || {};
      const healedChecklist = {};
      for (const [k, v] of Object.entries(rawChecklist)) {
        const norm = typeof v === 'string' ? v.trim().toLowerCase() : v;
        healedChecklist[k] = (norm === 'completed') ? 'Complete' : v;
      }
      setFormData({ ...lead, property_checklist_info: healedChecklist });
    }
  }, [lead, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/account-plans`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => (r.ok ? r.json() : { data: [] }))
      .then(d => setAccountPlans(Array.isArray(d?.data) ? d.data : []))
      .catch(() => setAccountPlans([]));
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e, nested = null) => {
    const { name, value } = e.target;
    
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: { ...(prev[nested] || {}), [name]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOTAChange = (index, field, value) => {
    const otaInfo = [...(formData.ota_info || [])];
    if (!otaInfo[index]) otaInfo[index] = getEmptyOTAItem();
    otaInfo[index][field] = value;
    setFormData(prev => ({ ...prev, ota_info: otaInfo }));
  };

  // iter246 — Multi-contact helpers for Edit dialog (mirrors Add Account modal).
  const EDIT_MAX_CONTACTS = 5;
  const editAddPhoneRow = () => setFormData(prev => {
    const list = Array.isArray(prev.additional_phones) ? prev.additional_phones : [];
    if (list.length >= EDIT_MAX_CONTACTS - 1) return prev;
    return { ...prev, additional_phones: [...list, ''] };
  });
  const editRemovePhoneRow = (idx) => setFormData(prev => ({
    ...prev, additional_phones: (prev.additional_phones || []).filter((_, i) => i !== idx),
  }));
  const editUpdateAdditionalPhone = (idx, v) => setFormData(prev => {
    const list = [...(prev.additional_phones || [])];
    list[idx] = v;
    return { ...prev, additional_phones: list };
  });
  const editSetPrimaryPhoneFromAdditional = (idx) => setFormData(prev => {
    const list = [...(prev.additional_phones || [])];
    const chosen = list[idx];
    list[idx] = prev.phone || '';
    return { ...prev, phone: chosen || '', additional_phones: list };
  });
  const editAddEmailRow = () => setFormData(prev => {
    const list = Array.isArray(prev.additional_emails) ? prev.additional_emails : [];
    if (list.length >= EDIT_MAX_CONTACTS - 1) return prev;
    return { ...prev, additional_emails: [...list, ''] };
  });
  const editRemoveEmailRow = (idx) => setFormData(prev => ({
    ...prev, additional_emails: (prev.additional_emails || []).filter((_, i) => i !== idx),
  }));
  const editUpdateAdditionalEmail = (idx, v) => setFormData(prev => {
    const list = [...(prev.additional_emails || [])];
    list[idx] = v;
    return { ...prev, additional_emails: list };
  });
  const editSetPrimaryEmailFromAdditional = (idx) => setFormData(prev => {
    const list = [...(prev.additional_emails || [])];
    const chosen = list[idx];
    list[idx] = prev.email || '';
    return { ...prev, email: chosen || '', additional_emails: list };
  });

  const addOTA = () => {
    const otaInfo = [...(formData.ota_info || [])];
    if (otaInfo.length < MAX_OTA_ENTRIES) {
      otaInfo.push(getEmptyOTAItem());
      setFormData(prev => ({ ...prev, ota_info: otaInfo }));
    }
  };

  const removeOTA = (index) => {
    const otaInfo = [...(formData.ota_info || [])];
    otaInfo.splice(index, 1);
    setFormData(prev => ({ ...prev, ota_info: otaInfo }));
  };

  // iter307 — Per-section field allow-lists. `handleSubmit` filters the
  // full `formData` down to just the keys owned by the section being
  // edited so we never submit unrelated fields that would incorrectly
  // trip the backend section-access lock. The 'all' section (full edit
  // dialog) still submits everything.
  const SECTION_FIELDS = {
    lead_info: [
      'property_status', 'lead_stage', 'hotel_name', 'owner_name',
      'phone', 'email', 'additional_phones', 'additional_emails',
      'gender', 'designation', 'email_id',
      'website', 'website_available', 'lead_owner', 'source',
      'no_of_rooms', 'no_of_properties', 'lead_date',
    ],
    address: [
      'address', 'city', 'state', 'country', 'pincode',
      'landmark_reference', 'document_collection',
    ],
    ota_challenges: [
      'existing_ota_connected', 'existing_ota_info',
      'ota_listing_link', 'key_challenges',
    ],
    payment_info: ['payment_info'],
    property_details: ['property_details'],
    document_collection: ['property_checklist_info'],
    property_checklist: ['property_checklist_info'],
    ota_info: ['ota_info'],
  };

  const filterFieldsBySection = (data, sec) => {
    if (!sec || sec === 'all') return data;
    const allow = SECTION_FIELDS[sec];
    if (!allow) return data;
    const out = {};
    for (const key of allow) {
      if (key in data) out[key] = data[key];
    }
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return; // ignore double clicks while saving
    setIsSaving(true);
    try {
      // Auto-capture completion_date for OTA entries on save
      if (section === 'ota_info' || section === 'all') {
        // iter312 — Validate listing_url on every OTA entry before we
        // save. Reject the whole save if any URL is malformed so bad
        // links (e.g. "www .example .com", "example", trailing spaces)
        // never enter the database. The normalizer + regex above still
        // accepts schemeless hosts like `example.com/x`.
        const urls = (formData.ota_info || []).map(ota => ota.listing_url);
        for (let i = 0; i < urls.length; i++) {
          const err = validateListingUrl(urls[i]);
          if (err) {
            toast.error(`OTA row ${i + 1}: ${err}`);
            setIsSaving(false);
            return;
          }
        }
        const now = new Date().toISOString().split('T')[0];
        const otaInfo = (formData.ota_info || []).map(ota => ({
          ...ota,
          // Trim listing_url so accidental leading/trailing whitespace
          // doesn't break normalizeHref() downstream.
          listing_url: (ota.listing_url || '').trim(),
          completion_date: ota.completion_date || (ota.listing_url ? now : '')
        }));
        const merged = { ...formData, ota_info: otaInfo };
        // iter307 — only send section-owned fields for `ota_info`. For
        // 'all' (full edit modal) keep the previous behaviour of
        // submitting the entire form so nothing is silently dropped.
        await onSave(section === 'all' ? merged : filterFieldsBySection(merged, section));
      } else {
        await onSave(filterFieldsBySection(formData, section));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const sectionTitles = {
    all: 'Lead',
    lead_info: 'Lead Information',
    address: 'Address Information',
    follow_up: 'Follow Up',
    ota_challenges: 'OTA & Challenges',
    payment_info: 'Commercials',
    property_details: 'Property Details',
    document_collection: 'Document Collection',
    property_checklist: 'Property Checklist Info',
    ota_info: 'OTA Information'
  };

  const renderFormFields = (sec = section) => {
    switch (sec) {
      case 'lead_info':
        return (
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Property Status" name="property_status" value={formData.property_status} onChange={handleChange} options={PROPERTY_STATUS_OPTIONS} />
            <SelectField label="Lead Stage" name="lead_stage" value={formData.lead_stage} onChange={handleChange} options={LEAD_STAGE_OPTIONS} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name</label>
              <input type="text" name="hotel_name" value={formData.hotel_name || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name</label>
              <input type="text" name="owner_name" value={formData.owner_name || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} placeholder="Primary mobile" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                  <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked readOnly className="w-4 h-4 accent-teal-600" /> Primary</label>
                  <span className="w-6" />
                </div>
                {(formData.additional_phones || []).map((v, i) => (
                  <div key={`edit-phone-${i}`} className="flex items-center gap-2" data-testid={`edit-phone-row-${i}`}>
                    <input type="tel" value={v || ''} onChange={(e) => editUpdateAdditionalPhone(i, e.target.value)} placeholder={`Additional mobile ${i + 1}`} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-phone-additional-input-${i}`} />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={false} onChange={() => editSetPrimaryPhoneFromAdditional(i)} className="w-4 h-4 accent-teal-600" data-testid={`edit-phone-primary-checkbox-${i}`} /> Primary
                    </label>
                    <button type="button" onClick={() => editRemovePhoneRow(i)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`edit-phone-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
                {((formData.additional_phones || []).length + 1) < EDIT_MAX_CONTACTS && (
                  <button type="button" onClick={editAddPhoneRow} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 text-teal-700 hover:bg-teal-50 rounded border border-teal-200" data-testid="edit-phone-add-btn">
                    <Plus size={12} /> Add Mobile Number ({(formData.additional_phones || []).length + 1}/{EDIT_MAX_CONTACTS})
                  </button>
                )}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="email" name="email" value={formData.email || ''} onChange={handleChange} placeholder="Primary email" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
                  <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked readOnly className="w-4 h-4 accent-teal-600" /> Primary</label>
                  <span className="w-6" />
                </div>
                {(formData.additional_emails || []).map((v, i) => (
                  <div key={`edit-email-${i}`} className="flex items-center gap-2" data-testid={`edit-email-row-${i}`}>
                    <input type="email" value={v || ''} onChange={(e) => editUpdateAdditionalEmail(i, e.target.value)} placeholder={`Additional email ${i + 1}`} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" data-testid={`edit-email-additional-input-${i}`} />
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={false} onChange={() => editSetPrimaryEmailFromAdditional(i)} className="w-4 h-4 accent-teal-600" data-testid={`edit-email-primary-checkbox-${i}`} /> Primary
                    </label>
                    <button type="button" onClick={() => editRemoveEmailRow(i)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`edit-email-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))}
                {((formData.additional_emails || []).length + 1) < EDIT_MAX_CONTACTS && (
                  <button type="button" onClick={editAddEmailRow} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 text-teal-700 hover:bg-teal-50 rounded border border-teal-200" data-testid="edit-email-add-btn">
                    <Plus size={12} /> Add Email Address ({(formData.additional_emails || []).length + 1}/{EDIT_MAX_CONTACTS})
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" name="website" value={formData.website || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <SelectField label="Website Available" name="website_available" value={formData.website_available} onChange={handleChange} options={WEBSITE_AVAILABLE_OPTIONS} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sales Owner</label>
              <input type="text" name="lead_owner" value={formData.lead_owner || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <LeadSourceField
              value={formData.source}
              onChange={(val) => setFormData(prev => ({ ...prev, source: val }))}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">No of Rooms</label>
              <input type="number" name="no_of_rooms" value={formData.no_of_rooms || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">No of Properties</label>
              <input type="number" name="no_of_properties" value={formData.no_of_properties || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Closed Won Date</label>
              <input
                type="date"
                name="lead_date"
                value={formData.lead_date?.split('T')[0] || ''}
                onChange={handleChange}
                disabled={!editIsAdmin}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${
                  !editIsAdmin ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300'
                }`}
                data-testid="edit-closed-won-date"
              />
              {!editIsAdmin && (
                <p className="text-xs text-gray-400 mt-1">Only Admin can edit Closed Won Date.</p>
              )}
            </div>
          </div>
        );

      case 'address':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <textarea name="address" value={formData.address || ''} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" name="city" value={formData.city || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <SelectField label="State" name="state" value={formData.state} onChange={handleChange} options={INDIAN_STATES} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input type="text" name="country" value={formData.country || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
              <input type="text" name="pincode" value={formData.pincode || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Landmark / Reference</label>
              <input type="text" name="landmark_reference" value={formData.landmark_reference || ''} onChange={handleChange} placeholder="e.g. Near Railway Station, Opposite City Mall" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" data-testid="edit-landmark-reference-input" />
            </div>
            {/* Phase 9 — Google Map Link moved here from Document Collection */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Map Link</label>
              <input
                type="url"
                name="google_map_link"
                value={formData.document_collection?.google_map_link || ''}
                onChange={(e) => handleChange(e, 'document_collection')}
                placeholder="https://maps.google.com/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="edit-google-map-link-input"
              />
            </div>
          </div>
        );

      case 'ota_challenges':
        return (
          <div className="grid grid-cols-1 gap-4">
            <SelectField label="Existing OTA Connected" name="existing_ota_connected" value={formData.existing_ota_connected} onChange={handleChange} options={EXISTING_OTA_OPTIONS} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Existing OTA Info</label>
              <textarea name="existing_ota_info" value={formData.existing_ota_info || ''} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
          </div>
        );

      case 'payment_info': {
        const pi = formData.payment_info || {};
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Interested Plan — dynamic dropdown; selecting a plan auto-fills One Time Setup Fee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interested Plan</label>
              <select
                name="interested_plan"
                value={pi.interested_plan || ''}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  const plan = accountPlans.find(p => p.name === selectedName);
                  setFormData(prev => {
                    const next = { ...prev, payment_info: { ...prev.payment_info, interested_plan: selectedName } };
                    if (plan) {
                      next.payment_info.one_time_setup_fee = Number(plan.price);
                    } else if (!selectedName) {
                      next.payment_info.one_time_setup_fee = '';
                    }
                    const fst = parseFloat(next.payment_info.finalize_setup_token);
                    const adv = parseFloat(next.payment_info.token_advance);
                    if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                      const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                      next.payment_info.pending_payment = Math.max(0, pending);
                    }
                    return next;
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid="edit-interested-plan-select"
              >
                <option value="">Select Plan</option>
                {accountPlans.map(p => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
              {accountPlans.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No plans configured. Admin can add them in Settings → Module Settings → Accounts → Plan Management.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Completed Date</label>
              <input type="date" name="payment_completed_date" value={pi.payment_completed_date?.split('T')[0] || ''} onChange={(e) => handleChange(e, 'payment_info')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            {/* One Time Setup Fee — auto-filled & read-only when a plan is selected */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                One Time Setup Fee
                {pi.interested_plan && (
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto-filled</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                readOnly={!!pi.interested_plan}
                value={pi.one_time_setup_fee ?? ''}
                onChange={(e) => {
                  if (pi.interested_plan) return;
                  const val = e.target.value;
                  setFormData(prev => ({ ...prev, payment_info: { ...prev.payment_info, one_time_setup_fee: val === '' ? '' : parseFloat(val) } }));
                }}
                placeholder={pi.interested_plan ? 'Set from plan' : '0.00'}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${pi.interested_plan ? 'bg-gray-50 border-gray-200 cursor-not-allowed' : 'border-gray-300'}`}
                data-testid="edit-one-time-setup-fee-input"
              />
            </div>
            {/* iter246 — "Finalize Setup Token" label renamed to "Finalize Setup".
                Backend field name (finalize_setup_token) is unchanged. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Finalize Setup</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pi.finalize_setup_token ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData(prev => {
                    const next = { ...prev, payment_info: { ...prev.payment_info, finalize_setup_token: raw === '' ? '' : parseFloat(raw) } };
                    const fst = parseFloat(next.payment_info.finalize_setup_token);
                    const adv = parseFloat(next.payment_info.token_advance);
                    if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                      const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                      const clamped = Math.max(0, pending);
                      next.payment_info.pending_payment = clamped;
                      const bothTouched = !Number.isNaN(fst) && !Number.isNaN(adv);
                      if (bothTouched) {
                        next.payment_info.setup_payment_status = clamped > 0 ? 'Pending' : 'Completed';
                      }
                    } else {
                      next.payment_info.pending_payment = '';
                    }
                    return next;
                  });
                }}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="edit-finalize-setup-token"
              />
              {(() => {
                const stored = pi.commission_discount_percentage;
                const hasStored = stored !== null && stored !== undefined && stored !== '' && !Number.isNaN(parseFloat(stored));
                const fee = parseFloat(pi.one_time_setup_fee);
                const fst = parseFloat(pi.finalize_setup_token);
                const computed = (!Number.isNaN(fee) && !Number.isNaN(fst) && fee > 0) ? ((fee - fst) / fee) * 100 : null;
                const disc = hasStored ? parseFloat(stored) : computed;
                if (disc === null || Number.isNaN(disc)) return null;
                const tone = disc >= 0 ? 'text-emerald-600' : 'text-red-600';
                return (
                  <p className={`text-xs mt-1 ${tone}`} data-testid="edit-discount-display">
                    Discount: <span className="font-semibold">{disc.toFixed(2)}%</span>{' '}
                    <span className="text-gray-400">{hasStored ? '(stored)' : '((Setup Fee − Finalize Setup Token) / Setup Fee × 100)'}</span>
                  </p>
                );
              })()}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Token Advance</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pi.token_advance ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  setFormData(prev => {
                    const next = { ...prev, payment_info: { ...prev.payment_info, token_advance: raw === '' ? '' : parseFloat(raw) } };
                    const fst = parseFloat(next.payment_info.finalize_setup_token);
                    const adv = parseFloat(next.payment_info.token_advance);
                    if (!Number.isNaN(fst) || !Number.isNaN(adv)) {
                      const pending = (Number.isNaN(fst) ? 0 : fst) - (Number.isNaN(adv) ? 0 : adv);
                      const clamped = Math.max(0, pending);
                      next.payment_info.pending_payment = clamped;
                      const bothTouched = !Number.isNaN(fst) && !Number.isNaN(adv);
                      if (bothTouched) {
                        next.payment_info.setup_payment_status = clamped > 0 ? 'Pending' : 'Completed';
                      }
                    } else {
                      next.payment_info.pending_payment = '';
                    }
                    return next;
                  });
                }}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="edit-token-advance-input"
              />
            </div>
            {/* iter246 — "Pending Payment" label renamed to "Setup Pending".
                Backend field name (pending_payment) is unchanged. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setup Pending
                <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto-calculated</span>
              </label>
              <input
                type="number"
                readOnly
                value={pi.pending_payment ?? ''}
                placeholder="Auto: Finalize Setup − Token Advance"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-teal-500 outline-none cursor-not-allowed"
                data-testid="edit-pending-payment-input"
              />
              <p className="text-xs text-gray-400 mt-1">= Finalize Setup − Token Advance</p>
            </div>
            {/* Setup Payment Status — auto-derived, read-only */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setup Payment Status
                <span className="ml-2 text-[10px] uppercase tracking-wide text-teal-600 font-semibold">Auto</span>
              </label>
              <select
                value={pi.setup_payment_status || ''}
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed"
                data-testid="edit-setup-payment-status-select"
                aria-readonly="true"
              >
                {SETUP_PAYMENT_STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Pending if Pending Payment &gt; 0; Completed when zero.</p>
            </div>
            {/* Commission Agreed (%) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commission Agreed (%)</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={pi.commission_agreed ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') { setFormData(prev => ({ ...prev, payment_info: { ...prev.payment_info, commission_agreed: '' } })); return; }
                    const num = parseFloat(raw);
                    if (Number.isNaN(num)) return;
                    setFormData(prev => ({ ...prev, payment_info: { ...prev.payment_info, commission_agreed: Math.max(0, Math.min(100, num)) } }));
                  }}
                  placeholder="e.g. 5, 10, 12.5"
                  className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                  data-testid="edit-commission-agreed-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm">%</span>
              </div>
            </div>
            {/* iter246 — Setup Clearing Terms hidden when Setup Payment Status = Completed.
                Stored value (setup_clearing_terms) is preserved for backward compat. */}
            {pi.setup_payment_status !== 'Completed' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Setup Clearing Terms</label>
                <select
                  name="setup_clearing_terms"
                  value={pi.setup_clearing_terms || ''}
                  onChange={(e) => handleChange(e, 'payment_info')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                  data-testid="edit-setup-clearing-terms-select"
                >
                  <option value="">Select</option>
                  <option value="Live Booking">Bookings</option>
                  <option value="Adjust">EMI</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">How the setup amount is being cleared.</p>
              </div>
            )}
          </div>
        );
      }

      case 'property_details':
        const pd_form = formData.property_details || {};
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input type="text" name="company_name" value={pd_form.company_name || ''} onChange={(e) => handleChange(e, 'property_details')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GST No</label>
              <input type="text" name="gst_no" value={pd_form.gst_no || ''} onChange={(e) => handleChange(e, 'property_details')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period Since Live on OTA (Years)</label>
              <input type="number" name="live_since_years" value={pd_form.live_since_years || ''} onChange={(e) => handleChange(e, 'property_details')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observed Occupancy (%)</label>
              <input type="number" name="observed_occupancy" value={pd_form.observed_occupancy || ''} onChange={(e) => handleChange(e, 'property_details')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" min="0" max="100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Occupancy (%)</label>
              <input type="number" name="expected_occupancy" value={pd_form.expected_occupancy || ''} onChange={(e) => handleChange(e, 'property_details')} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" min="0" max="100" />
            </div>
          </div>
        );

      case 'document_collection':
        const pc_dc = formData.property_checklist_info || {};
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Drive Link</label>
              <input type="url" name="document_drive_link" value={pc_dc.document_drive_link || ''} onChange={(e) => handleChange(e, 'property_checklist_info')} placeholder="https://drive.google.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" data-testid="edit-document-drive-link-input" />
            </div>
          </div>
        );

      case 'property_checklist':
        const pc = formData.property_checklist_info || {};
        return (
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Master Form" name="master_form" value={pc.master_form} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="NOC Agreement" name="noc_agreement" value={pc.noc_agreement} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Lease Agreement" name="lease_agreement" value={pc.lease_agreement} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Electricity Bill" name="electricity_bill" value={pc.electricity_bill} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="PAN Card" name="pan_card" value={pc.pan_card} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Aadhar Card" name="aadhar_card" value={pc.aadhar_card} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="GST Certificate" name="gst_certificate" value={pc.gst_certificate} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Cancelled Cheque & Bank Details" name="cancelled_check_and_bank_details" value={pc.cancelled_check_and_bank_details} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Property Photos" name="property_photos" value={pc.property_photos} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
            <SelectField label="Document Collection Status" name="document_collection_status" value={pc.document_collection_status} onChange={(e) => handleChange(e, 'property_checklist_info')} options={CHECKLIST_STATUS_OPTIONS} />
          </div>
        );

      case 'ota_info':
        const otaList = formData.ota_info || [];
        return (
          <div className="space-y-4">
            {otaList.map((ota, index) => {
              // iter313 — Lock badge + disabled inputs should only render
              // once the OTA row has actual data to protect. When the
              // `listing_url` is empty/null the row is essentially
              // unfilled, so treat it as editable regardless of the
              // server-side `locked` flag. Whitespace-only counts as
              // empty so a copy-paste of spaces can't accidentally
              // lock a fresh row.
              const hasListingUrl = !!(ota.listing_url && String(ota.listing_url).trim());
              const otaLocked = !!ota.locked && hasListingUrl;
              return (
              <div key={index} className={`p-4 rounded-lg border relative ${otaLocked ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                {otaLocked && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full" data-testid={`ota-edit-locked-${index}`}>
                    <Lock size={11} /> Locked
                  </div>
                )}
                {!otaLocked && (
                  <button type="button" onClick={() => removeOTA(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500">
                    <X size={16} />
                  </button>
                )}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OTA Name</label>
                    <select
                      value={ota.ota_name || ''}
                      onChange={(e) => handleOTAChange(index, 'ota_name', e.target.value)}
                      disabled={otaLocked}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}
                      data-testid={`ota-name-${index}`}
                    >
                      {/* Iter239 — dropdown reads from the dynamic OTA config
                          (Settings → Module Settings → Hotel Onboarded → OTA
                          Configuration). Hide OTAs already picked by OTHER
                          rows but always keep the current row's saved value
                          selectable, even if it's been removed from the config. */}
                      <option value="">Select OTA</option>
                      {(() => {
                        const takenByOtherRows = new Set(
                          otaList.filter((o, i) => i !== index && o.ota_name).map(o => o.ota_name)
                        );
                        const visible = dynamicOtaOptions.filter(
                          opt => opt.value === ota.ota_name || !takenByOtherRows.has(opt.value)
                        );
                        if (ota.ota_name && !visible.some(o => o.value === ota.ota_name)) {
                          visible.push({ value: ota.ota_name, label: `${ota.ota_name} (removed)` });
                        }
                        return visible.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ));
                      })()}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OTA Status</label>
                    <select value={ota.ota_status || ''} onChange={(e) => {
                      const next = e.target.value;
                      // iter285 — Gate "Integrated" behind both Hotel Link + Hotel ID.
                      if (next === 'Integrated') {
                        const hid = (ota.hotel_id || '').trim();
                        const hlink = (ota.hotel_link || '').trim();
                        if (!hid || !hlink) {
                          toast.error('Fill Hotel Link and Hotel ID before marking Integrated');
                          return;
                        }
                      }
                      handleOTAChange(index, 'ota_status', next);
                    }} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}>
                      {OTA_STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                      {/* Preserve any status not in the legacy list (e.g. a
                          "Self Manage" OTA added via Add Account) so editing
                          here never silently blanks it. */}
                      {ota.ota_status && !OTA_STATUS_OPTIONS.some(o => o.value === ota.ota_status) && (
                        <option value={ota.ota_status}>{ota.ota_status}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel ID</label>
                    <input type="text" value={ota.hotel_id || ''} onChange={(e) => handleOTAChange(index, 'hotel_id', e.target.value)} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Link</label>
                    <input type="url" value={ota.hotel_link || ''} onChange={(e) => handleOTAChange(index, 'hotel_link', e.target.value)} disabled={otaLocked} placeholder="https://" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} data-testid={`ota-hotel-link-${index}`} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Listing URL</label>
                    <input type="url" value={ota.listing_url || ''} onChange={(e) => handleOTAChange(index, 'listing_url', e.target.value)} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} placeholder="https://" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Access to Hotel</label>
                    <select value={ota.access_to_hotel || ''} onChange={(e) => handleOTAChange(index, 'access_to_hotel', e.target.value)} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}>
                      {ACCESS_TO_HOTEL_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Completion Status</label>
                    <input type="text" value={ota.completion_status || ''} onChange={(e) => handleOTAChange(index, 'completion_status', e.target.value)} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">QC Owner</label>
                    <input type="text" value={ota.qc_owner || ''} onChange={(e) => handleOTAChange(index, 'qc_owner', e.target.value)} disabled={otaLocked} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Property Name on OTA</label>
                    <input type="text" value={ota.property_name_on_ota || ''} onChange={(e) => handleOTAChange(index, 'property_name_on_ota', e.target.value)} disabled={otaLocked} placeholder="Name as listed on OTA" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none ${otaLocked ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`} data-testid={`ota-property-name-${index}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Completion Date</label>
                    <input type="date" value={ota.completion_date ? ota.completion_date.substring(0, 10) : ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed" data-testid={`ota-completion-date-${index}`} />
                    <p className="text-xs text-gray-400 mt-0.5">{otaLocked ? 'Auto-captured on submit' : 'Auto-captured on save'}</p>
                  </div>
                </div>
              </div>
              );
            })}
            {otaList.length < MAX_OTA_ENTRIES && (
              <button type="button" onClick={addOTA} className="flex items-center gap-2 text-teal-600 hover:text-teal-700">
                <Plus size={16} /> Add OTA Entry ({otaList.length}/{MAX_OTA_ENTRIES})
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Item 10 — render EVERY account section stacked (parity with Add Account).
  const ALL_LEAD_SECTIONS = ['lead_info', 'address', 'payment_info', 'property_details', 'property_checklist', 'ota_info'];
  const renderAllSections = () => (
    <div className="space-y-8" data-testid="edit-lead-all-sections">
      {ALL_LEAD_SECTIONS.map((sec) => (
        <section key={sec} data-testid={`edit-lead-section-${sec}`}>
          <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">{sectionTitles[sec]}</h3>
          {renderFormFields(sec)}
        </section>
      ))}
      {/* Bank Details — self-saving card */}
      <section data-testid="edit-lead-section-bank">
        <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">Bank Details</h3>
        <BankDetailsCard leadId={lead?.id} initialData={lead?.bankDetails || {}} />
      </section>
      {/* PEF — self-saving card */}
      <section data-testid="edit-lead-section-pef">
        <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">PEF</h3>
        <PefPdfCard leadId={lead?.id} initialData={lead?.documentCollection || {}} />
      </section>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-800">Edit {sectionTitles[section]}</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {section === 'all' ? renderAllSections() : renderFormFields()}
        </form>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={isSaving} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center disabled:opacity-60 disabled:cursor-not-allowed">
            {isSaving ? (
              <span className="mr-2 inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Save size={18} className="mr-2" />
            )}
            {isSaving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, leadName, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="text-red-600" size={28} />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Delete Lead</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete <span className="font-semibold text-gray-800">"{leadName}"</span>? 
            This action cannot be undone and all associated data will be permanently removed.
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
              data-testid="cancel-delete-lead-btn"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center transition-colors disabled:opacity-50"
              data-testid="confirm-delete-lead-btn"
            >
              <Trash2 size={18} className="mr-2" />
              {loading ? 'Deleting...' : 'Delete Lead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Lead Details Page
const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  // iter319 — Mirror backend `_is_access_enabled`'s fallback to
  // `leads.edit` when no explicit `section_access_config` row exists.
  const { canEdit } = usePermissions();
  
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [editModal, setEditModal] = useState({ open: false, section: null });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [syncingAdmin, setSyncingAdmin] = useState(false);
  const [otaListings, setOtaListings] = useState([]);
  // Phase 9 — id→name lookup for the OTA Information "CX" column.
  const [userNameMap, setUserNameMap] = useState({});
  // Phase 18b — legacy field visibility toggle. Older accounts (pre-Phase
  // 18a.1) still carry Property Status / Website / Website Available /
  // No of Properties / Closed Won Date. Hidden by default; toggle on
  // demand when reviewing legacy records.
  const [showLegacyFields, setShowLegacyFields] = useState(false);

  // iter246 — GTM Sales edit-access workflow state.
  // - editAccessRequestModal: local prompt to submit a new request.
  // - editAccessDecisionModal: shown to Admin / gtm_sales_leader when a
  //   pending edit-access notification is present or deep-linked via
  //   ?edit_access_req=<id>.
  const [editAccessRequestModal, setEditAccessRequestModal] = useState({ open: false, section: null });
  const [editAccessDecisionModal, setEditAccessDecisionModal] = useState({ open: false, request: null });
  const [editAccessSubmitting, setEditAccessSubmitting] = useState(false);

  // Data lock: iter329 — legacy blanket locks retired for users holding
  // `leads.edit`. Backend now enforces the intended commercials/OTA/etc.
  // exceptions per-section via services/section_access.py. The two flags
  // below are kept for tooltip messaging on roles WITHOUT leads.edit —
  // e.g. view-only users. Never applied to a user who has been granted
  // the Edit permission by Admin.
  const caStatus = lead?.commercial_approval?.status;
  const userRole = (user?.role || '').toLowerCase();
  const isAdmin = userHasRole(user, 'admin');
  const isExecutive = userHasRole(user, 'executive');
  const _hasLeadsEdit = !!canEdit('leads');
  const isLeadLocked = caStatus === 'verified' && !isExecutive && !isAdmin && !_hasLeadsEdit;
  // ACCOUNT LOCK (Approved): once an account is auto-locked, users without
  // an explicit `leads.edit` grant fall back to read-only. Admin/Executive
  // and any role Admin has granted `leads.edit` remain editable — matches
  // the backend `_is_access_enabled` precedence in section_access.py.
  const accountLocked = !!lead?.account_locked && !isAdmin && !isExecutive && !_hasLeadsEdit;

  // FIELD-LEVEL LOCKING: Lead Information section
  // iter279 — multi-role safe edit-access gate.
  //   Grant edit if the user holds ANY of these roles:
  //     • gtm_sales, gtm_sales_leader, cx, cx_leader
  //   `userHasAnyRole` transparently supports:
  //     - `user.roles` array (iter264+ multi-role users)
  //     - `user.role` scalar OR comma/semicolon/whitespace-separated string
  //   So a user assigned e.g. ['qc', 'cx'] receives edit access via `cx`
  //   without losing any QC functionality (QC-only users still cannot edit).
  //   Admin ALWAYS retains edit access; GTM Sales Leader can edit before the
  //   permanent lead-info lock; the other three edit-granting roles remain
  //   editable while the commercial-approval workflow has not started.
  const EDIT_ACCESS_ROLES = ['gtm_sales', 'gtm_sales_leader', 'cx', 'cx_leader'];
  const hasEditAccessRole = userHasAnyRole(user, EDIT_ACCESS_ROLES);
  const leadInfoLocked = lead?.lead_info_locked;
  const canEditLeadInfo = (() => {
    if (isAdmin) return true;
    if (accountLocked) return false; // Approved & locked — non-admin/exec read-only
    // iter329 — Lead Information workflow lock only fires for GTM Sales
    // (the role it was designed for). Any other role with leads.edit
    // (e.g. CX, CX Leader, Exec, or an Admin-granted custom role) may
    // still correct Lead Info after GTM Leader verification.
    if (leadInfoLocked && userHasRole(user, 'gtm_sales')) return false;
    if (userHasRole(user, 'gtm_sales_leader')) return true; // GTM Leader can edit before lock
    // Users with an explicit leads.edit grant may edit Lead Info even
    // after the commercial-approval workflow has started (backend allows
    // this since iter329; frontend must mirror to keep the pencil live).
    if (_hasLeadsEdit) return true;
    // Legacy role-based grant for pre-permission-system users.
    if (hasEditAccessRole && (!caStatus || caStatus === 'none')) return true;
    return false; // All other roles cannot edit Lead Information
  })();

  // Commercials section (general card edit button)
  // Rules:
  //   - Admin: ALWAYS
  //   - gtm_sales: NEVER via general card (uses CommercialApproval workflow)
  //   - When approval workflow active: hide general edit for non-admin
  //   - gtm_sales_leader / cx / cx_leader: allowed pre-approval (iter279)
  const canEditCommercialsCard = (() => {
    if (isAdmin) return true;
    if (accountLocked) return false; // Approved & locked — non-admin/exec read-only
    if (userHasRole(user, 'gtm_sales')) return false;
    if (caStatus && caStatus !== 'none') return false; // Use approval workflow edit
    if (userHasAnyRole(user, ['gtm_sales_leader', 'cx', 'cx_leader'])) return true;
    return false;
  })();

  useEffect(() => {
    fetchLead();
  }, [id]);

  // Section Access Control (iter221) — load role/section rules once.
  // Available to all roles because the frontend needs to know which pencils
  // to hide; write access is admin-only on the backend.
  const [sectionAccessConfig, setSectionAccessConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;
    settingsAPI.getSectionAccess().then(res => {
      if (!cancelled) setSectionAccessConfig(res.data || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Fetch OTA listings for this lead
  useEffect(() => {
    if (id) {
      otaAPI.getLeadSummary(id).then(res => {
        setOtaListings(res.data?.listings || []);
      }).catch(() => {});
    }
  }, [id, loading]);

  // Phase 9 — load a lightweight user directory so the OTA Information
  // table can show the assigned CX user's name instead of their UUID.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/lookup`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const map = {};
        (list || []).forEach(u => { if (u?.id) map[u.id] = u.name || u.id; });
        setUserNameMap(map);
      })
      .catch(() => { /* non-fatal; column simply falls back to '-' */ });
  }, [token]);

  const fetchLead = async () => {
    try {
      setLoading(true);
      const response = await leadsAPI.getById(id);
      setLead(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
      if (error.response?.status === 404) {
        navigate('/leads');
      }
    } finally {
      setLoading(false);
    }
  };

  // OTA Timer update callback - updates local state without full refetch
  const handleOTATimerUpdate = useCallback((otaIndex, updates) => {
    setLead(prev => {
      if (!prev) return prev;
      const otaInfo = [...(prev.ota_info || [])];
      if (otaInfo[otaIndex]) {
        otaInfo[otaIndex] = { ...otaInfo[otaIndex], ...updates };
      }
      return { ...prev, ota_info: otaInfo };
    });
  }, []);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await leadsAPI.delete(id);
      navigate('/leads');
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Failed to delete lead');
      setDeleting(false);
    }
  };

  // Manual Superhost Admin sync / re-sync for this account.
  const handleSyncToAdmin = async () => {
    try {
      setSyncingAdmin(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/superhost-admin/sync/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(`Synced to Superhost Admin${data.admin_property_id ? ` · Property ${data.admin_property_id}` : ''}`);
      } else if (res.ok && !data.success) {
        toast.error(`Sync failed: ${data.error || `HTTP ${data.http_status || ''}`}`);
      } else {
        toast.error(data.detail || 'Sync failed');
      }
      fetchLead();
    } catch (e) {
      toast.error('Sync request failed');
    } finally {
      setSyncingAdmin(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!lead) return;
    
    try {
      setGeneratingPdf(true);
      
      // Create PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 15;
      
      // Header with teal color
      doc.setFillColor(13, 148, 136);
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      // Title
      doc.setFontSize(22);
      doc.setTextColor(255, 255, 255);
      doc.text('Lead Details Report', 14, 18);
      
      // Subtitle
      doc.setFontSize(12);
      doc.text(lead.hotel_name || 'Unnamed Lead', 14, 27);
      
      // Generated date
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth - 14, 27, { align: 'right' });
      
      yPos = 45;
      
      // Section helper function
      const addSection = (title, data) => {
        // Check if we need a new page
        if (yPos > 260) {
          doc.addPage();
          yPos = 15;
        }
        
        // Section header
        doc.setFontSize(14);
        doc.setTextColor(13, 148, 136);
        doc.text(title, 14, yPos);
        yPos += 3;
        
        // Underline
        doc.setDrawColor(13, 148, 136);
        doc.setLineWidth(0.5);
        doc.line(14, yPos, pageWidth - 14, yPos);
        yPos += 6;
        
        // Data rows
        doc.setFontSize(10);
        data.forEach(([label, value]) => {
          if (yPos > 275) {
            doc.addPage();
            yPos = 15;
          }
          doc.setTextColor(100);
          doc.text(label + ':', 14, yPos);
          doc.setTextColor(40);
          doc.text(String(value || '-'), 70, yPos);
          yPos += 6;
        });
        
        yPos += 5;
      };
      
      // Lead Information
      addSection('Lead Information', [
        ['Hotel Name', lead.hotel_name],
        ['Owner Name', lead.owner_name],
        ['Mobile Number', lead.phone],
        ['Email', lead.email],
        ['Lead Stage', lead.lead_stage],
        ['Property Status', lead.property_status],
        ['Lead Source', lead.source],
        ['Sales Owner', lead.lead_owner],
        ['Closed Won Date', lead.lead_date ? new Date(lead.lead_date).toLocaleDateString() : null]
      ]);
      
      // Address Information
      addSection('Address Information', [
        ['Address', lead.address],
        ['City', lead.city],
        ['State', lead.state],
        ['Country', lead.country],
        ['Pincode', lead.pincode],
        ['Landmark / Reference', lead.landmark_reference]
      ]);
      
      // Property Details
      addSection('Property Details', [
        ['No. of Rooms', lead.no_of_rooms],
        ['No. of Properties', lead.no_of_properties],
        ['Website', lead.website],
        ['Website Available', lead.website_available]
      ]);
      
      // OTA Information
      addSection('OTA Information', [
        ['Existing OTA', lead.existing_ota_connected],
        ['OTA Info', lead.existing_ota_info],
        ['OTA Listing Link', lead.ota_listing_link],
        ['Key Challenges', lead.key_challenges]
      ]);
      
      // Commercials
      addSection('Commercials', [
        ['Interested Plan', lead.payment_info?.interested_plan],
        ['Payment Completed Date', lead.payment_info?.payment_completed_date ? new Date(lead.payment_info.payment_completed_date).toLocaleDateString() : null],
        ['One Time Setup Fee', lead.payment_info?.one_time_setup_fee ? `₹${lead.payment_info.one_time_setup_fee}` : null],
        ['Finalize Setup', lead.payment_info?.finalize_setup_token ? `₹${lead.payment_info.finalize_setup_token}` : null],
        ['Commission Agreed %', lead.payment_info?.commission_agreed != null && lead.payment_info?.commission_agreed !== '' ? `${lead.payment_info.commission_agreed}%` : null],
        ['Token Advance', lead.payment_info?.token_advance ? `₹${lead.payment_info.token_advance}` : null],
        ['Setup Pending', lead.payment_info?.pending_payment ? `₹${lead.payment_info.pending_payment}` : null],
        ['Setup Payment Status', lead.payment_info?.setup_payment_status],
      ]);
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Generated by Superhost CRM | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
      
      // Save PDF
      const filename = `${lead.hotel_name?.replace(/[^a-z0-9]/gi, '_') || 'lead'}_details.pdf`;
      doc.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSaveSection = async (updatedData) => {
    try {
      await leadsAPI.update(id, updatedData);
      setEditModal({ open: false, section: null });
      fetchLead();
    } catch (error) {
      // iter246 — Defensive save-time handling of the GTM Sales edit-access
      // gate. If the status changed between opening and saving (e.g. a
      // parallel edit consumed the grant), the backend returns 403 with
      // detail.code='edit_access_required'. In that case, close the edit
      // form and open the "Additional Edit Access Required" popup so the
      // user can re-request. All other errors surface the actual backend
      // message so users see what really failed (spec: expose real error
      // instead of a generic failure).
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      if (status === 403 && code === 'edit_access_required') {
        setEditModal({ open: false, section: null });
        setEditAccessRequestModal({ open: true, section: null, pendingRequestId: null });
        return;
      }
      // Surface the real message when the backend gave us one.
      let message = 'Failed to update lead';
      if (typeof detail === 'string' && detail) {
        message = detail;
      } else if (detail && typeof detail === 'object' && detail.message) {
        message = detail.message;
      } else if (error?.message) {
        message = error.message;
      }
      console.error('Error updating lead:', status, detail, error);
      toast.error(message);
    }
  };

  const openEditModal = (section) => {
    if (isLeadLocked) return; // Global verified lock
    if (accountLocked) return; // Account approved & locked (non-admin/exec)
    if (section === 'lead_info' && !canEditLeadInfo) return;
    if (section === 'payment_info' && !canEditCommercialsCard) return;
    // iter246 — GTM Sales edit-access gate. Query the backend once; if the
    // user has exhausted their one free edit AND has no active grant, show
    // the request popup instead of opening the edit form.
    if ((user?.role || '').toLowerCase() === 'gtm_sales') {
      (async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/status?lead_id=${encodeURIComponent(lead.id)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data && data.can_edit === false) {
              setEditAccessRequestModal({ open: true, section, pendingRequestId: data.pending_request_id || null });
              return;
            }
          }
        } catch (_) { /* silent — fall through to open the modal */ }
        setEditModal({ open: true, section });
      })();
      return;
    }
    setEditModal({ open: true, section });
  };

  // iter246 — Submit an edit-access request from the request-required popup.
  const submitEditAccessRequest = async () => {
    if (!lead?.id || editAccessSubmitting) return;
    setEditAccessSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lead_id: lead.id }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.already_open
          ? 'You already have a pending edit request for this account.'
          : 'Edit access request sent to Admin / GTM Sales Leader.');
        setEditAccessRequestModal({ open: false, section: null });
      } else {
        toast.error(data.detail || 'Could not submit edit access request.');
      }
    } catch (_) {
      toast.error('Could not submit edit access request.');
    } finally {
      setEditAccessSubmitting(false);
    }
  };

  // iter246 — Approver decision (grant / reject).
  const decideEditAccess = async (grant) => {
    const req = editAccessDecisionModal.request;
    if (!req) return;
    setEditAccessSubmitting(true);
    try {
      const action = grant ? 'grant' : 'reject';
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/${req.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success(grant ? 'Edit access granted.' : 'Edit access rejected.');
        setEditAccessDecisionModal({ open: false, request: null });
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not update request.');
      }
    } catch (_) {
      toast.error('Could not update request.');
    } finally {
      setEditAccessSubmitting(false);
    }
  };

  // iter246 — Auto-open the approver decision popup when the account URL
  // carries ?edit_access_req=<id> (deep-link from a notification) OR when
  // the current user is an approver and this account has an open request.
  useEffect(() => {
    if (!lead?.id) return;
    const role = (user?.role || '').toLowerCase();
    const isApprover = role === 'admin' || role === 'gtm_sales_leader';
    if (!isApprover) return;
    const params = new URLSearchParams(window.location.search);
    const reqId = params.get('edit_access_req');
    (async () => {
      try {
        if (reqId) {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/${reqId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const doc = await res.json();
            if (doc && doc.status === 'pending' && doc.lead_id === lead.id) {
              setEditAccessDecisionModal({ open: true, request: doc });
              return;
            }
          }
        }
        // No deep-link ID — fall back to listing pending requests and
        // opening the one for this account (if any).
        const res2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/edit-access/pending`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res2.ok) {
          const data = await res2.json();
          const match = (data?.data || []).find(r => r.lead_id === lead.id && r.status === 'pending');
          if (match) setEditAccessDecisionModal({ open: true, request: match });
        }
      } catch (_) { /* silent */ }
    })();
    // Depend only on the lead id + user role so re-renders don't re-fetch.
  }, [lead?.id, user?.role, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    const num = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(num)) return '-';
    // iter246 — strip unnecessary trailing .00 for whole-number amounts.
    // Preserves meaningful decimals when they are non-zero.
    const isWhole = Math.abs(num - Math.round(num)) < 0.005;
    if (isWhole) {
      return `₹${Math.round(num).toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };


  const handleSendEmail = async () => {
    if (!emailForm.subject.trim()) return;
    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      // Prefer same-origin so Gmail send hits whatever custom production
      // domain the CRM is being served on (e.g. comp.extranetsync.com) —
      // otherwise the stale REACT_APP_BACKEND_URL in the baked bundle would
      // route the send to the preview backend and use its preview
      // redirect_uri. See GmailIntegrationSettings.jsx for the same pattern.
      const apiBase = (typeof window !== 'undefined' && window.location && window.location.origin)
        ? window.location.origin
        : process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/gmail/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: lead.email, subject: emailForm.subject, body: emailForm.body, lead_id: lead.id }),
      });
      const data = await res.json();
      if (data.status === 'success') { alert('Email sent successfully!'); setEmailModal(false); setEmailForm({ subject: '', body: '' }); }
      else alert(data.message || 'Failed to send email');
    } catch { alert('Failed to send email'); }
    finally { setEmailSending(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)] text-gray-500">
        <Building2 size={48} className="text-gray-300 mb-4" />
        <p className="text-lg font-medium">Lead not found</p>
        <button onClick={() => navigate('/leads')} className="mt-4 text-teal-600 hover:text-teal-800">
          Back to Leads
        </button>
      </div>
    );
  }

  const pi = lead.payment_info || {};
  const pd = lead.property_details || {};
  const dc = lead.document_collection || {};
  const pc = lead.property_checklist_info || {};
  const otaInfo = lead.ota_info || [];
  // Self-Manage OTAs are hotel-managed and surface ONLY under "Existing OTA
  // Connected" — exclude them from the OTA Information display table (they
  // remain editable via the Edit popup, and excluded from all workflows).
  const displayOtaInfo = otaInfo.filter(
    (o) => (o?.ota_status || '').trim().toLowerCase() !== 'self manage'
  );

  // ── Final-QC 5-step Admin verifications (additive) ──────────────
  // The 4 buttons render only for Admin. Server also enforces admin + the
  // per-step readiness checks; the client only gates the button for a
  // friendlier UX (grey/disabled + tooltip).
  const adminVerifications = lead.admin_verifications || {};
  const isVerified = (step) => !!(adminVerifications[step] && adminVerifications[step].verified);
  const bd = lead.bankDetails || {};
  const docc = lead.documentCollection || lead.document_collection || {};
  const readyCommercials = !!(docc.pefPdfUrl || docc.pefPdfName);
  const _cmp = (v) => (typeof v === 'string') && v.trim().toLowerCase() === 'complete';
  const readyDocs = _cmp(pc.aadhar_card) && _cmp(pc.pan_card) && _cmp(pc.cancelled_check_and_bank_details) && _cmp(pc.property_photos);
  const otaApplicable = (o) => {
    if (!o || typeof o !== 'object') return false;
    const name = (o.ota_name || '').trim();
    if (!name || ['--select--','select','none'].includes(name.toLowerCase())) return false;
    if ((o.ota_status || '').trim().toLowerCase() === 'self manage') return false;
    return true;
  };
  const otaCompleted = (o) => ['ota_status','listing_status','completion_status']
    .some(k => ['completed','complete','live'].includes((o[k] || '').trim().toLowerCase()));
  const applicableOtas = (otaInfo || []).filter(otaApplicable);
  const readyOta = applicableOtas.length > 0 && applicableOtas.every(otaCompleted);
  const readyBank = !!(bd.cancelledChequePdfUrl) || (!!bd.accountHolderName && !!bd.accountNumber && !!bd.ifscCode);

  // ── Section Access Control (iter221) ──────────────────────────────
  // Determine whether the current logged-in user is allowed to edit a
  // given section on THIS lead. Admin/Executive always allowed. Non-Admins
  // need: (a) explicit role/user-level enablement in the config AND
  // (b) they have not yet submitted this section for this lead. GTM Sales
  // is additionally gated by Lead-Owner. Mirrors the backend rules in
  // `services/section_access.py` so UX matches API behaviour.
  const _sacUserId = (user?.id || '').toLowerCase();
  const _sacRole = (userRole || '').toLowerCase();
  const _sacRules = sectionAccessConfig?.rules || [];
  const _sacIsSectionEnabled = (section) => {
    const s = (section || '').toLowerCase();
    // 1. User-level override wins over everything (admin escape hatch —
    //    matches backend `_is_access_enabled` in section_access.py).
    const userRule = _sacRules.find(r => r.scope === 'user' && (r.key || '').toLowerCase() === _sacUserId && r.section === s);
    if (userRule) return !!userRule.enabled;
    // 2. iter329 — Effective `leads.edit` grant unlocks the section (was
    //    previously evaluated AFTER the role rule, which caused a false
    //    UI lock when a legacy `role/<role>/<section>` row existed with
    //    `enabled:false`). Now mirrors backend precedence exactly.
    if (canEdit('leads')) return true;
    // 3. Legacy role-level default (only applies when the user does NOT
    //    hold an effective `leads.edit` grant).
    const roleRule = _sacRules.find(r => r.scope === 'role' && (r.key || '').toLowerCase() === _sacRole && r.section === s);
    if (roleRule) return !!roleRule.enabled;
    // Fallback matches backend: no rule and no leads.edit → locked.
    return false;
  };
  const _firstSubmits = lead.section_first_submits || {};
  // iter318 — Sections permanently exempt from the first-submit lock.
  // Mirrors backend `UNLOCKED_SECTIONS` in `services/section_access.py`
  // so the UI never grays out a control the backend would happily
  // accept. Add to this set to permanently un-lock a section on both
  // ends without any other code change.
  const UNLOCKED_SECTIONS = new Set(['property_checklist_info']);
  // iter325 — Commercial Approval post-admin-approval gate. Mirrors the
  // backend rule in services/section_access.py — once admin_approved,
  // only Admin can touch the commercials section.
  const _caStatus = String((lead.commercial_approval || {}).status || '').toLowerCase();
  const canEditSection = (section) => {
    if (['admin', 'executive'].includes(_sacRole)) return true;
    // Config not loaded yet → keep existing (legacy) permissions to avoid
    // hiding controls on initial paint.
    if (!sectionAccessConfig) return true;
    if (!_sacIsSectionEnabled(section)) return false;
    // iter325 — Commercial Approval post-admin lock (only intentional
    // workflow restriction remaining after this refactor).
    if (section === 'commercials' && _caStatus === 'admin_approved') return false;
    // iter325 — GTM Sales owner check removed. Admin-granted Edit
    // permission is now authoritative; ownership is preserved only as
    // an audit trail (section_first_submits).
    // iter325 — First-submit UI lock removed. The backend no longer
    // enforces it either — a `leads.edit` grant permits re-editing any
    // number of times. First-submit records are kept for audit only.
    return true;
  };
  const _canEditCommercials = canEditSection('commercials');
  const _canEditChecklist = canEditSection('property_checklist_info');
  const _canEditOta = canEditSection('ota_information');
  const _canEditBank = canEditSection('bank_details');

  const handleAdminVerify = async (step, ready, disallowReason) => {
    if (!ready) {
      toast.error(disallowReason || 'Requirements not met for this verification.');
      return;
    }
    try {
      await leadsAPI.verifyStep(id, step);
      toast.success('Verified.');
      // Merge locally to avoid a full refetch (endpoint returns success only).
      setLead(prev => prev ? ({
        ...prev,
        admin_verifications: {
          ...(prev.admin_verifications || {}),
          [step]: { verified: true, verified_at: new Date().toISOString(), verified_by_name: user?.name || user?.email }
        }
      }) : prev);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed.');
    }
  };

  const AdminVerifyBtn = ({ step, label, ready, reason, testid }) => {
    if (!isAdmin) return null;
    const verified = isVerified(step);
    if (verified) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
          title={`Verified by ${(adminVerifications[step] || {}).verified_by_name || 'admin'}`}
          data-testid={`${testid}-verified`}
        >
          <Check size={12} /> Verified
        </span>
      );
    }
    return (
      <button
        onClick={() => handleAdminVerify(step, ready, reason)}
        disabled={!ready}
        title={ready ? `Mark ${label} verified for Final QC` : reason}
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
          ready
            ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
            : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
        }`}
        data-testid={testid}
      >
        <Check size={12} /> {label}
      </button>
    );
  };

  const renderLeadsTab = () => (
    <div className="space-y-6">
      {/* Data Lock Banners */}
      {isLeadLocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="lead-locked-banner">
          <Lock size={18} className="text-amber-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">This lead is verified and locked</p>
            <p className="text-xs text-amber-600">Only Executive users can make edits to verified leads.</p>
          </div>
        </div>
      )}
      {!canEditLeadInfo && !isLeadLocked && !isAdmin && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="lead-info-locked-banner">
          <Lock size={18} className="text-slate-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Lead Information is read-only</p>
            <p className="text-xs text-slate-500">
              You do not have Edit permission on Accounts. Contact your Admin to request Edit access.
            </p>
          </div>
        </div>
      )}

      {accountLocked && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="account-locked-banner">
          <Lock size={18} className="text-emerald-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">This account is approved &amp; locked</p>
            <p className="text-xs text-emerald-600">All mandatory documents and OTA listings are complete. Only Admin or Executive can make edits.</p>
          </div>
        </div>
      )}

      {/* Commercial Approval Workflow */}
      <CommercialApproval lead={lead} onLeadUpdated={fetchLead} />
      
      {/* Phase 18a — 50-50 split: sticky PEF on left, scrollable details on right.
          On lg+ screens the left column stays fixed while the right column
          scrolls; on small screens both stack vertically. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="lead-details-split-layout">
      <aside className="lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto space-y-4" data-testid="lead-details-pef-panel">
        <PefPdfCard leadId={lead.id} initialData={lead.documentCollection || {}} />
      </aside>
      <div className="space-y-6" data-testid="lead-details-scrollable">
      {/* Lead Info Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            Lead Information
            {!canEditLeadInfo && !isAdmin && (
              <span title="Locked after GTM Leader verification. Only Admin can edit." className="text-gray-400"><Lock size={14} /></span>
            )}
          </span>
          {canEditLeadInfo && (
            <button onClick={() => openEditModal('lead_info')} className="text-teal-600 hover:text-teal-800" data-testid="edit-lead-info-btn">
              <Edit2 size={16} />
            </button>
          )}
        </div>
      }>
        <InfoRow label="Lead Stage" value={<StatusBadge value={lead.lead_stage} type="stage" />} />
        {/* iter246 — Closed Won Date positioned between Lead Stage and Hotel Name per spec. */}
        <InfoRow label="Closed Won Date" value={formatDate(lead.lead_date)} />
        <InfoRow label="Hotel Name" value={lead.hotel_name} />
        <InfoRow label="Owner Name" value={lead.owner_name} />
        <InfoRow label="Mobile Number" value={lead.phone} />
        {/* iter246 — Additional mobile numbers surfaced immediately after the primary,
            before Email Address. Preserves the primary mapping (lead.phone) unchanged. */}
        {Array.isArray(lead.additional_phones) && lead.additional_phones.filter(Boolean).map((p, i) => (
          <InfoRow key={`add-phone-${i}`} label={`Mobile Number ${i + 2}`} value={p} data-testid={`lead-info-additional-phone-${i}`} />
        ))}
        <InfoRow label="Email Address" value={lead.email} />
        {/* iter246 — Additional email addresses surfaced immediately after the primary,
            before Sales Owner. Preserves the primary mapping (lead.email) unchanged. */}
        {Array.isArray(lead.additional_emails) && lead.additional_emails.filter(Boolean).map((e, i) => (
          <InfoRow key={`add-email-${i}`} label={`Email Address ${i + 2}`} value={e} data-testid={`lead-info-additional-email-${i}`} />
        ))}
        <InfoRow label="Sales Owner" value={lead.lead_owner} />
        <InfoRow label="Lead Source" value={lead.source} />
        <InfoRow label="No of Rooms" value={lead.no_of_rooms} />

        {/* Phase 18b — legacy fields, hidden by default. */}
        {showLegacyFields && (
          <>
            <InfoRow label="Property Status" value={<StatusBadge value={lead.property_status} type="status" />} />
            <InfoRow label="Website" value={lead.website} isLink />
            <InfoRow label="Website Available" value={lead.website_available} />
            <InfoRow label="No of Properties" value={lead.no_of_properties} />
          </>
        )}

        <button
          type="button"
          onClick={() => setShowLegacyFields(v => !v)}
          className="mt-3 text-xs font-medium text-teal-600 hover:text-teal-700 underline-offset-2 hover:underline"
          data-testid="toggle-legacy-fields"
        >
          {showLegacyFields ? 'Hide legacy fields' : 'Show legacy fields'}
        </button>
        
        {/* Labels Section */}
        <div className="pt-4 mt-4 border-t border-gray-100">
          <LabelSelector
            entityId={lead.id}
            entityType="lead"
            selectedLabels={lead.labels || []}
            onLabelsChange={(newLabels) => setLead({ ...lead, labels: newLabels })}
            editable={true}
          />
        </div>
      </InfoCard>

      {/* Address Info Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span>Address Information</span>
          <button onClick={() => openEditModal('address')} disabled={isLeadLocked || accountLocked} className={`${(isLeadLocked || accountLocked) ? 'text-gray-300 cursor-not-allowed' : 'text-teal-600 hover:text-teal-800'}`} data-testid="edit-address-btn">
            <Edit2 size={16} />
          </button>
        </div>
      }>
        <InfoRow label="Address" value={lead.address} />
        <InfoRow label="City" value={lead.city} />
        <InfoRow label="State" value={lead.state} />
        <InfoRow label="Country" value={lead.country} />
        <InfoRow label="Pincode" value={lead.pincode} />
        <InfoRow label="Landmark / Reference" value={lead.landmark_reference} />
        {/* Phase 9 — Google Map Link relocated from Document Collection */}
        <InfoRow label="Google Map Link" value={dc.google_map_link} isLink />
      </InfoCard>

      {/* OTA & Challenges Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span>OTA &amp; Challenges</span>
          <button onClick={() => openEditModal('ota_challenges')} disabled={isLeadLocked || accountLocked} className={`${(isLeadLocked || accountLocked) ? 'text-gray-300 cursor-not-allowed' : 'text-teal-600 hover:text-teal-800'}`} data-testid="edit-ota-challenges-btn">
            <Edit2 size={16} />
          </button>
        </div>
      }>
        <InfoRow label="Existing OTA Connected" value={<StatusBadge value={lead.existing_ota_connected} type="status" />} />
        <InfoRow label="Existing OTA Info" value={lead.existing_ota_info} />
      </InfoCard>

      {/* Commercials Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            Commercials
            {!canEditCommercialsCard && userRole === 'gtm_sales' && (
              <span title="Locked after creation. Only GTM Leader or Admin can edit." className="text-gray-400"><Lock size={14} /></span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <AdminVerifyBtn step="commercials" label="Verify Commercials"
              ready={readyCommercials}
              reason="Upload the PEF PDF before verifying Commercials."
              testid="admin-verify-commercials-btn" />
            {canEditCommercialsCard && _canEditCommercials && (
              <button onClick={() => openEditModal('payment_info')} className="text-teal-600 hover:text-teal-800" data-testid="edit-payment-btn">
                <Edit2 size={16} />
              </button>
            )}
          </div>
        </div>
      }>
        <InfoRow label="Interested Plan" value={<StatusBadge value={pi.interested_plan} type="status" />} />
        <InfoRow label="Payment Completed Date" value={formatDate(pi.payment_completed_date)} />
        <InfoRow label="One Time Setup Fee" value={formatCurrency(pi.one_time_setup_fee)} />
        {/* iter246 — "Finalize Setup Token" display renamed to "Finalize Setup" and now
            shown with ₹ symbol. Backend field name (finalize_setup_token) is unchanged. */}
        <InfoRow label="Finalize Setup" value={formatCurrency(pi.finalize_setup_token)} />
        {/* RCA-1 — Discount %: stored `commission_discount_percentage` is source of truth;
            fall back to the calculated value only when the stored field is empty (legacy leads). */}
        <InfoRow
          label="Discount %"
          value={(() => {
            const stored = pi.commission_discount_percentage;
            if (stored != null && stored !== '' && !Number.isNaN(parseFloat(stored))) return `${parseFloat(stored).toFixed(2)}%`;
            const fee = parseFloat(pi.one_time_setup_fee);
            const fst = parseFloat(pi.finalize_setup_token);
            if (!Number.isNaN(fee) && !Number.isNaN(fst) && fee > 0) return `${(((fee - fst) / fee) * 100).toFixed(2)}%`;
            return '-';
          })()}
        />
        {/* Phase 9 — surface the Commission Agreed % captured during Create */}
        <InfoRow
          label="Commission Agreed %"
          value={formatCommission(pi.commission_agreed)}
        />
        <InfoRow label="Token Advance" value={formatCurrency(pi.token_advance)} />
        {/* iter246 — "Pending Payment" display renamed to "Setup Pending".
            Backend field name (pending_payment) is unchanged. */}
        <InfoRow label="Setup Pending" value={formatCurrency(pi.pending_payment)} />
        <InfoRow label="Setup Payment Status" value={<StatusBadge value={pi.setup_payment_status} type="status" />} />
        {/* iter246 — Setup Clearing Terms is hidden when Setup Payment Status = Completed.
            Stored value (payment_info.setup_clearing_terms) is preserved for backward compat. */}
        {pi.setup_payment_status !== 'Completed' && (
          <InfoRow label="Setup Clearing Terms" value={setupClearingTermsLabel(pi.setup_clearing_terms)} />
        )}
      </InfoCard>

      {/* Property Details Card removed from UI (data preserved in DB) per spec. */}

      {/* Document Collection Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span>Document Collection</span>
          <button onClick={() => openEditModal('document_collection')} disabled={isLeadLocked || accountLocked} className={`${(isLeadLocked || accountLocked) ? 'text-gray-300 cursor-not-allowed' : 'text-teal-600 hover:text-teal-800'}`} data-testid="edit-documents-btn">
            <Edit2 size={16} />
          </button>
        </div>
      }>
        <InfoRow label="Document Drive Link" value={pc.document_drive_link} isLink />
        <InfoRow label="Document Drive Link Updated" value={formatDate(dc.document_drive_link_updated_time)} />
      </InfoCard>

      {/* PEF PDF Card moved to sticky left panel (Phase 18a) */}

      {/* Property Checklist Info Card - with SLA Tracking */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span>Property Checklist Info</span>
          <div className="flex items-center gap-2">
            <AdminVerifyBtn step="docs" label="Verify Docs"
              ready={readyDocs}
              reason="Aadhaar, PAN, Bank Details and Property Photos must be Complete (Master Form is not required for this step)."
              testid="admin-verify-docs-btn" />
            <button onClick={() => openEditModal('property_checklist')} disabled={isLeadLocked || accountLocked || !_canEditChecklist} className={`${(isLeadLocked || accountLocked || !_canEditChecklist) ? 'text-gray-300 cursor-not-allowed' : 'text-teal-600 hover:text-teal-800'}`} data-testid="edit-checklist-btn" title={!_canEditChecklist ? 'Section locked — you have already submitted this section (or you do not have access). Contact Admin.' : undefined}>
              <Edit2 size={16} />
            </button>
          </div>
        </div>
      } className="lg:col-span-2">
        {/* SLA Tracker */}
        <ChecklistSLATracker leadId={lead.id} token={token} />

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Master Form</p>
            <StatusBadge value={pc.master_form} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">NOC Agreement</p>
            <StatusBadge value={pc.noc_agreement} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Lease Agreement</p>
            <StatusBadge value={pc.lease_agreement} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Electricity Bill</p>
            <StatusBadge value={pc.electricity_bill} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">PAN Card</p>
            <StatusBadge value={pc.pan_card} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Aadhar Card</p>
            <StatusBadge value={pc.aadhar_card} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">GST Certificate</p>
            <StatusBadge value={pc.gst_certificate} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Bank Details</p>
            <StatusBadge value={pc.cancelled_check_and_bank_details} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Property Photos</p>
            <StatusBadge value={pc.property_photos} type="status" />
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Collection Status</p>
            <StatusBadge value={pc.document_collection_status} type="status" />
          </div>
        </div>

        {/* Auto-generated Pending Documents list — drives the WhatsApp
            {{lead.pending_documents}} template variable. Pure read-only;
            recomputed from the checklist statuses above on every render. */}
        {(() => {
          const PENDING_DOC_LABELS = [
            ['master_form', 'Master Form'],
            ['noc_agreement', 'NOC Agreement'],
            ['lease_agreement', 'Lease Agreement'],
            ['electricity_bill', 'Electricity Bill'],
            ['pan_card', 'PAN Card'],
            ['aadhar_card', 'Aadhaar Card'],
            ['gst_certificate', 'GST'],
            ['cancelled_check_and_bank_details', 'Cancelled Cheque & Bank Details'],
            ['property_photos', 'Property Photos'],
          ];
          const pending = PENDING_DOC_LABELS
            .filter(([k]) => String(pc[k] || '').trim().toLowerCase() === 'pending')
            .map(([, label]) => label);
          return (
            <div className="mt-5 pt-4 border-t border-gray-100" data-testid="pending-documents-section">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Pending Documents
                </span>
                <code className="text-[10px] text-gray-400">{'{{lead.pending_documents}}'}</code>
              </div>
              <p
                className={`mt-1 text-sm ${pending.length ? 'text-amber-700 font-medium' : 'text-gray-400 italic'}`}
                data-testid="pending-documents-value"
              >
                {pending.length ? pending.join(', ') : 'No pending documents'}
              </p>
            </div>
          );
        })()}
      </InfoCard>

      {/* Bank Details Card - NEW */}
      <BankDetailsCard
        leadId={lead.id}
        initialData={lead.bankDetails || {}}
        onSaved={fetchLead}
        editAllowed={_canEditBank}
        headerExtra={
          <AdminVerifyBtn step="bank" label="Verify Bank Details"
            ready={readyBank}
            reason="Add core bank details or a cancelled cheque before verifying."
            testid="admin-verify-bank-btn" />
        }
      />

      {/* OTA Info Table Card */}
      <InfoCard title={
        <div className="flex items-center justify-between">
          <span>OTA Information</span>
          <div className="flex items-center gap-2">
            <AdminVerifyBtn step="ota" label="Verify OTA Links"
              ready={readyOta}
              reason="Every applicable (non-Self-Manage) OTA must be completed."
              testid="admin-verify-ota-btn" />
            <button onClick={() => openEditModal('ota_info')} disabled={isLeadLocked || accountLocked || !_canEditOta} className={`${(isLeadLocked || accountLocked || !_canEditOta) ? 'text-gray-300 cursor-not-allowed' : 'text-teal-600 hover:text-teal-800'}`} data-testid="edit-ota-info-btn" title={!_canEditOta ? 'Section locked — you have already submitted this section (or you do not have access). Contact Admin.' : undefined}>
              <Edit2 size={16} />
            </button>
          </div>
        </div>
      } className="lg:col-span-2">
        {displayOtaInfo.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">OTA Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Listing Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Hotel ID</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Listing URL</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Completion Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">CX</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">QC</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayOtaInfo.map((ota, index) => (
                  <tr key={index} className={`hover:bg-gray-50 ${ota.locked ? 'bg-green-50/30' : ''}`} data-testid={`ota-row-${index}`}>
                    <td className="px-3 py-2 font-medium">
                      <span className="flex items-center gap-1.5">
                        {ota.locked && <Lock size={12} className="text-green-500 flex-shrink-0" />}
                        {ota.ota_name || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-2" data-testid={`ota-listing-status-${index}`}>
                      {(ota.ota_status || '').toUpperCase().includes('INTEGRATION') ? (
                        <span className="inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full bg-blue-50 text-blue-700">Integration</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full bg-purple-50 text-purple-700">New Listing</span>
                      )}
                    </td>
                    <td className="px-3 py-2"><StatusBadge value={ota.ota_status} type="status" /></td>
                    <td className="px-3 py-2 text-gray-600">{ota.hotel_id || '-'}</td>
                    <td className="px-3 py-2">
                      {ota.listing_url ? (
                        <a href={normalizeHref(ota.listing_url)} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 flex items-center gap-1" data-testid={`ota-listing-url-${index}`}>
                          View <ExternalLink size={12} />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-600" data-testid={`ota-completion-date-${index}`}>
                      {ota.completion_date ? formatDate(ota.completion_date) : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-700" data-testid={`ota-cx-${index}`}>
                      {ota.completed_by || ota.submitted_by_name || (ota.assigned_to ? (userNameMap[ota.assigned_to] || ota.assigned_to) : '-')}
                    </td>
                    <td className="px-3 py-2 text-gray-700" data-testid={`ota-qc-${index}`}>
                      {ota.qc_completed_by_name || (ota.qc_assigned_to ? (userNameMap[ota.qc_assigned_to] || ota.qc_assigned_to) : '-')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No OTA information available. Click edit to add OTA entries.</p>
        )}
      </InfoCard>

      {/* OTA Listings Checklist (Phase 2) */}
      {otaListings.length > 0 && (
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="ota-listings-checklist-section">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-800">OTA Listings Checklist</h3>
            <p className="text-xs text-gray-500 mt-0.5">Checklist SLA tracking for assigned OTA listings</p>
          </div>
          <div className="p-5 space-y-3">
            {otaListings.map(listing => (
              <div key={listing.id} className="space-y-2" data-testid={`ota-listing-checklist-${listing.id}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700">{listing.ota_name}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">{listing.assigned_to || 'Unassigned'}</span>
                  <span className="text-gray-400">|</span>
                  <span className={`px-2 py-0.5 rounded-full border font-medium ${
                    listing.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                    listing.status === 'in_progress' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                    'bg-blue-100 text-blue-700 border-blue-200'
                  }`}>{listing.status}</span>
                </div>
                <OTAChecklistSLATracker
                  otaId={listing.id}
                  initialSla={listing.checklist_sla}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Phase 19.3 — Custom Fields & Diligence sections permanently
          removed from the Account Details UI. Backend data, custom_fields
          API, and the Settings → Account Form Builder remain intact;
          existing records keep their persisted values. */}

      </div>{/* /right scrollable column (Phase 18a) */}
    </div>
    </div>
  );

  const renderPlaceholderTab = (tabName) => (
    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
      <div className="text-gray-400 mb-4">
        <FileText size={48} className="mx-auto" />
      </div>
      <h3 className="text-lg font-medium text-gray-600 mb-2">{tabName}</h3>
      <p className="text-gray-500">This section is under development.</p>
    </div>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] space-y-4" data-testid="lead-details-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/leads')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-testid="back-to-leads-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{lead.hotel_name}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              {lead.grb_property_id && (
                <span className="inline-block px-2 py-0.5 text-xs font-bold tracking-wide rounded bg-teal-50 text-teal-700 font-mono" data-testid="grb-id-badge">
                  {lead.grb_property_id}
                </span>
              )}
              {lead.owner_name && <span className="flex items-center gap-1"><User size={14} /> {lead.owner_name}</span>}
              {lead.phone && <span className="flex items-center gap-1"><Phone size={14} /> {lead.phone}</span>}
              {lead.city && <span className="flex items-center gap-1"><MapPin size={14} /> {lead.city}</span>}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {lead.email && (
            <button onClick={() => setEmailModal(true)} className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors" data-testid="send-email-btn">
              <Mail size={18} className="mr-2" /> Send Email
            </button>
          )}
          {(isAdmin || userRole === 'executive') && (
            <button
              onClick={handleSyncToAdmin}
              disabled={syncingAdmin}
              className="flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50"
              data-testid="superhost-admin-sync-btn"
              title={lead.superhost_admin_sync?.status === 'Success'
                ? `Last synced ${lead.superhost_admin_sync?.last_synced_at ? new Date(lead.superhost_admin_sync.last_synced_at).toLocaleString() : ''}`
                : 'Push this account to the Superhost Admin Panel'}
            >
              {syncingAdmin ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Server size={18} className="mr-2" />}
              {syncingAdmin
                ? 'Syncing...'
                : (lead.superhost_admin_sync?.status ? 'Re-sync to Superhost Admin' : 'Sync to Superhost Admin')}
            </button>
          )}
          <button
            onClick={() => openEditModal('all')}
            disabled={!canEditLeadInfo}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors shadow-sm ${!canEditLeadInfo ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
            data-testid="edit-lead-btn"
            title={!canEditLeadInfo
              ? (leadInfoLocked
                  ? 'Locked after GTM Leader verification. Only Admin can edit.'
                  : 'Editing not permitted for your role at the current stage.')
              : ''}
          >
            {!canEditLeadInfo ? <Lock size={18} className="mr-2" /> : <Edit2 size={18} className="mr-2" />}
            {/* Phase 18b.2 — Show "Edit" label even when disabled so the
                affordance is consistent across roles; the lock icon + the
                disabled state communicate the restriction. */}
            Edit Lead
          </button>
          <button onClick={() => setDeleteModal(true)} disabled={deleting} className="flex items-center px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50" data-testid="delete-lead-btn">
            <Trash2 size={18} className="mr-2" />
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
          <button onClick={handleDownloadPdf} disabled={generatingPdf} className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50" data-testid="download-pdf-btn">
            <Download size={18} className="mr-2" />
            {generatingPdf ? 'Generating...' : 'Download PDF'}
          </button>
          <button className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
            <MoreHorizontal size={20} />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b border-gray-200 bg-gray-50 rounded-t-lg px-2">
        <TabButton label="Leads" active={activeTab === 'leads'} onClick={() => setActiveTab('leads')} testId="tab-leads" />
        <TabButton label="Tasks" active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')} testId="tab-tasks" />
        <TabButton label="Notes" active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} testId="tab-notes" />
        <TabButton label="Visits" active={activeTab === 'visits'} onClick={() => setActiveTab('visits')} testId="tab-visits" />
        <TabButton label="Calls" active={activeTab === 'calls'} onClick={() => setActiveTab('calls')} testId="tab-calls" />
        <TabButton label="History" active={activeTab === 'history'} onClick={() => setActiveTab('history')} testId="tab-history" />
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto pb-6">
        {activeTab === 'leads' && renderLeadsTab()}
        {activeTab === 'tasks' && <LeadTasksTab leadId={id} />}
        {activeTab === 'notes' && renderPlaceholderTab('Notes')}
        {activeTab === 'visits' && renderPlaceholderTab('Visits')}
        {activeTab === 'calls' && renderPlaceholderTab('Calls')}
        {activeTab === 'history' && renderPlaceholderTab('History')}
      </div>

      {/* Edit Modal */}
      <EditLeadModal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, section: null })}
        onSave={handleSaveSection}
        lead={lead}
        section={editModal.section}
      />

      {/* iter246 — GTM Sales edit-access request popup. Shown when a gtm_sales
          user tries to edit an account after using their one free edit. */}
      {editAccessRequestModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="edit-access-request-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <Lock size={18} className="text-amber-500" /> Additional Edit Access Required
              </h3>
              <button onClick={() => setEditAccessRequestModal({ open: false, section: null })} className="text-gray-400 hover:text-gray-600" data-testid="edit-access-request-close-btn"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">
                You have already used your one free edit for <strong>{lead?.hotel_name}</strong>.
                To edit this account again, please request access from Admin or GTM Sales Leader.
              </p>
              {editAccessRequestModal.pendingRequestId && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  You already have a pending request for this account.
                </p>
              )}
            </div>
            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => setEditAccessRequestModal({ open: false, section: null })}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium"
                data-testid="edit-access-request-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitEditAccessRequest}
                disabled={editAccessSubmitting || !!editAccessRequestModal.pendingRequestId}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center gap-2 disabled:opacity-60"
                data-testid="edit-access-request-send-btn"
              >
                {editAccessSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter246 — Approver decision popup (Admin / gtm_sales_leader). Auto-opens
          when the account URL carries ?edit_access_req=<id> or when there is an
          open pending request for this account. */}
      {editAccessDecisionModal.open && editAccessDecisionModal.request && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="edit-access-decision-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <AlertCircle size={18} className="text-amber-500" /> Edit Access Request
              </h3>
              <button
                onClick={() => setEditAccessDecisionModal({ open: false, request: null })}
                className="text-gray-400 hover:text-gray-600"
                data-testid="edit-access-decision-close-btn"
              ><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-700">
                <strong data-testid="edit-access-decision-requester">{editAccessDecisionModal.request.requester_name}</strong> needs access to edit information
                for <strong>{editAccessDecisionModal.request.lead_name || lead?.hotel_name}</strong>.
              </p>
              <p className="text-xs text-gray-500">
                Requested: {editAccessDecisionModal.request.created_at ? new Date(editAccessDecisionModal.request.created_at).toLocaleString() : ''}
              </p>
            </div>
            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <button
                onClick={() => decideEditAccess(false)}
                disabled={editAccessSubmitting}
                className="px-4 py-2 border border-red-200 text-red-700 hover:bg-red-50 rounded-lg font-medium disabled:opacity-60"
                data-testid="edit-access-decision-reject-btn"
              >
                Not Grant Access
              </button>
              <button
                onClick={() => decideEditAccess(true)}
                disabled={editAccessSubmitting}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center gap-2 disabled:opacity-60"
                data-testid="edit-access-decision-grant-btn"
              >
                {editAccessSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Grant Access
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        leadName={lead?.hotel_name}
        loading={deleting}
      />

      {/* Send Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" data-testid="email-compose-modal">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 flex items-center gap-2"><Mail size={18} className="text-blue-500" /> Send Email</h3>
              <button onClick={() => setEmailModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">To</label>
                <input type="text" disabled value={lead?.email || ''} className="w-full px-3 py-2 text-sm bg-gray-50 border rounded-lg text-gray-600" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Subject</label>
                <input type="text" value={emailForm.subject} onChange={e => setEmailForm(p => ({...p, subject: e.target.value}))}
                  placeholder="Email subject" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-teal-500 outline-none" data-testid="email-subject-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Body</label>
                <textarea value={emailForm.body} onChange={e => setEmailForm(p => ({...p, body: e.target.value}))}
                  placeholder="Email body (HTML supported)" rows={6} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-teal-500 outline-none resize-y" data-testid="email-body-input" />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setEmailModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleSendEmail} disabled={emailSending || !emailForm.subject.trim()}
                className="flex items-center gap-1 px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50" data-testid="email-send-btn">
                {emailSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetails;
